import { X } from "lucide-react";
import { useEffect, useRef, type ReactNode } from "react";
import { cn } from "./cn";

export interface DialogAction {
  label: string;
  onClick: () => void | Promise<void>;
  variant?: "primary" | "secondary" | "danger" | "success";
  disabled?: boolean;
}

export interface DialogProps {
  open: boolean;
  onClose: () => void;
  title: string;
  description?: string;
  children?: ReactNode;
  actions?: ReactNode;
  /** `lg` for forms and grids, `xl` for a grid that needs the width, `sm` for confirmations. */
  size?: "sm" | "md" | "lg" | "xl";
}

const SIZES = {
  sm: "max-w-sm",
  md: "max-w-lg",
  lg: "max-w-3xl",
  xl: "max-w-[min(90rem,95vw)]",
} as const;

/**
 * Built on the native `<dialog>` element rather than a portal-and-focus-trap
 * library: `showModal()` already gives us the top layer, a focus trap, inert
 * background content and Esc-to-close, all with correct semantics.
 *
 * On mobile it becomes a bottom sheet, which is where a thumb can reach it.
 */
export function Dialog({
  open,
  onClose,
  title,
  description,
  children,
  actions,
  size = "md",
}: DialogProps) {
  const ref = useRef<HTMLDialogElement>(null);

  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;

    if (open && !dialog.open) {
      dialog.showModal();
    } else if (!open && dialog.open) {
      dialog.close();
    }
  }, [open]);

  // Esc fires the dialog's own `cancel`; route it back through onClose so the
  // caller's state stays in step with what the DOM is doing.
  useEffect(() => {
    const dialog = ref.current;
    if (!dialog) return;

    const onCancel = (event: Event) => {
      event.preventDefault();
      onClose();
    };
    dialog.addEventListener("cancel", onCancel);
    return () => dialog.removeEventListener("cancel", onCancel);
  }, [onClose]);

  return (
    <dialog
      ref={ref}
      onClick={(event) => {
        // Clicking the backdrop closes. The dialog element itself covers only
        // the panel, so a click landing on it means the backdrop was hit.
        if (event.target === ref.current) onClose();
      }}
      className={cn(
        // `m-auto` is what centres a modal <dialog> in the top layer, and it
        // has to be restated: Tailwind's preflight zeroes margin on every
        // element, which overrides the UA stylesheet and pins the dialog to
        // the top-left corner.
        "m-auto max-h-[calc(100dvh-2rem)] w-full bg-transparent p-0 text-ink backdrop:bg-black/50",
        // On small screens it becomes a bottom sheet instead.
        "max-md:mb-0 max-md:mt-auto max-md:max-h-[90dvh] max-md:max-w-none",
        SIZES[size],
      )}
    >
      <div
        className={cn(
          "flex max-h-[85vh] flex-col overflow-hidden rounded-lg border border-line bg-surface shadow-overlay",
          "max-md:rounded-b-none",
        )}
      >
        <header className="flex items-start gap-3 border-b border-line px-5 py-3.5">
          <div className="min-w-0 flex-1">
            <h2 className="text-[15px] font-semibold text-ink">{title}</h2>
            {description && <p className="mt-0.5 text-[13px] text-ink-muted">{description}</p>}
          </div>
          <button
            type="button"
            onClick={onClose}
            aria-label="Close"
            className="-mr-1 rounded p-1 text-ink-faint hover:bg-surface-sunk hover:text-ink"
          >
            <X className="size-4" />
          </button>
        </header>

        {children && (
          <div
            className={cn(
              "min-h-0 flex-1 overflow-auto px-5 py-4",
              // Form controls fill the modal rather than sitting at whatever
              // width suited them on a page: a `w-44` date input left two
              // thirds of the dialog empty. Scoped to Fields on purpose, so
              // compact controls that are not part of a form layout — a
              // numeric cell in a table, a toolbar select — are untouched.
              // In a row of Fields they each ask for the full width and shrink
              // to share it, which is why min-w-0 is here too.
              "[&_[data-field]]:w-full [&_[data-field]]:min-w-0",
              "[&_[data-field]_input]:w-full [&_[data-field]_select]:w-full",
              "[&_[data-field]_textarea]:w-full",
            )}
          >
            {children}
          </div>
        )}

        {actions && (
          <footer className="flex items-center justify-end gap-2 border-t border-line px-5 py-3">
            {actions}
          </footer>
        )}
      </div>
    </dialog>
  );
}
